import * as Api from "@api";
import * as C from "@components";
import React from "react";
import VadImage from "vadimages-nextjs-image-optimizer";
import {HeroCardImageAlignment, HeroCardImageSize, ImageCardImageIndentBasic, Patent} from '@components';
import {altFromHtml} from "@/helpers/alt";

export const HeroSectionKeys = [
    ["hero.eyebrow"],
    "hero.headline",
    ["hero.subhead"],
    ["hero.body"],
    ["hero.link"],
] as const satisfies Api.ContentKey[];

interface HeroSectionLayout extends C.ImageCardLayoutBasic { }

export interface HeroSectionProps
{
    bgColor?: C.Color | string;
    contentProps: Omit<C.PageContentBlockProps<typeof HeroSectionKeys>, "keys">;
    imageProps?: Partial<React.ComponentProps<typeof VadImage>>;
    imageDirectory: string;
    layout?: C.MediaSpecific<HeroSectionLayout>;
    overlapped?: boolean;
    textColor?: C.Color | string;
    mobileImageProps?: Partial<React.ComponentProps<typeof VadImage>>;
    patent?: string
    imageIndent?: C.MediaSpecific<ImageCardImageIndentBasic>
    heroImageAlignment?: HeroCardImageAlignment,
    heroImageSize?: HeroCardImageSize
}

export const HeroSection = ({ bgColor, contentProps, imageIndent, imageDirectory, imageProps, layout, overlapped, textColor, mobileImageProps, patent, heroImageAlignment, heroImageSize }: HeroSectionProps): React.FunctionComponentElement<HeroSectionProps> =>
{
    const itemEyebrow = contentProps.items?.["hero.eyebrow"];
    const itemHeadline = contentProps.items?.["hero.headline"];
    const itemSubhead = contentProps.items?.["hero.subhead"];
    const itemBody = contentProps.items?.["hero.body"];
    const itemLink = contentProps.items?.["hero.link"];

    const contentPropsApplied: C.PageContentBlockProps<typeof HeroSectionKeys> = {
        ...contentProps,
        keys: HeroSectionKeys,
        items: {
            "hero.eyebrow": { ...itemEyebrow, props: { typography: C.Typography.H8, ...(itemEyebrow !== undefined && "props" in itemEyebrow ? itemEyebrow.props : undefined) } },
            "hero.headline": { ...itemHeadline, props: { layout: { mobile: { margins: { bottom: C.Margin.D20 } }, ipad: { margins: C.Margin.D0 } }, typography: C.Typography.H1, ...(itemHeadline !== undefined && "props" in itemHeadline ? itemHeadline.props : undefined) } },
            "hero.subhead": { ...itemSubhead, props: { typography: C.Typography.H5, ...(itemSubhead !== undefined && "props" in itemSubhead ? itemSubhead.props : undefined) } },
            "hero.body": { ...itemBody, props: { typography: C.Typography.P1, ...(itemBody !== undefined && "props" in itemBody ? itemBody.props : undefined) } },
            "hero.link": itemLink,
        },
    }

    const imagePropsApplied: React.ComponentProps<typeof VadImage> = {
        ...imageProps,
        src: imageProps?.src ?? `${imageDirectory}/hero.png`,
        alt: imageProps?.alt ?? altFromHtml(contentProps.pageContent["hero.headline"]?.data),
    };

    if(mobileImageProps !== undefined && mobileImageProps.width !== undefined && mobileImageProps.height !== undefined) {
        imagePropsApplied.mobileSrc = mobileImageProps?.src !== undefined ? mobileImageProps.src.toString() : `${imageDirectory}mobile/hero.png`;
        imagePropsApplied.mobileWidth = Number(mobileImageProps.width);
        imagePropsApplied.mobileHeight = Number(mobileImageProps.height);
    }

    const layoutApplied: C.MediaSpecific<HeroSectionLayout> = {
        ...layout,
        ipad: {
            mainAxisOffset: 98,
            ...layout?.ipad,
        },
    }

    return (
        <C.PageSection variant={C.PageSectionVariant.Hero}>
            <C.ImageCard
                bgColor={bgColor}
                contentProps={contentPropsApplied}
                imagePosition={C.CardImagePosition.Right}
                imageProps={imagePropsApplied}
                layout={layoutApplied}
                sideProps={{ mobileImageAlignment: C.HorizontalAlignment.Center, mobileImagePosition: C.CardImagePosition.Top, overlapped }}
                textColor={textColor}
                imageIndent={imageIndent}
                isHeroSection={true}
                heroImageAlignment={heroImageAlignment}
                heroImageSize={heroImageSize}
            />
            <Patent patent={patent} />
        </C.PageSection>
    );
}